import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import jsPDF from "jspdf";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { get, getAllPages } from "lib/api";
import { useCurrency } from "context/CurrencyContext";
import { useAuth } from "context/AuthContext";
import MobileTopBar from "mobile/components/MobileTopBar";
import { currencyForCompany, normalizeCompanyKey } from "lib/companyProfile";

const ymKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const monthLabel = (ym) => {
  const [y, m] = String(ym).split("-").map((x) => Number(x));
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

const numberOrZero = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const normalizeEntity = (d) => (d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : d);

const normalizeInventoryItemForProjection = (raw) => {
  const r = normalizeEntity(raw);
  if (!r) return null;
  const coreRel = r?.coreItem?.data || r?.coreItem || null;
  const coreN = coreRel ? normalizeEntity(coreRel) : null;
  const coreItemNormalized = coreN
    ? {
        ...coreN,
        unitCost: numberOrZero(coreN?.unitCost),
        coreCost: numberOrZero(coreN?.coreCost),
        quantityOnHand: numberOrZero(coreN?.quantityOnHand),
        quantityReserved: numberOrZero(coreN?.quantityReserved),
      }
    : null;

  // Core cost should come from the linked core inventory item whenever possible.
  // Prefer coreItem.unitCost (the actual purchase cost). Fallback to coreItem.coreCost for legacy data.
  // If no coreItem is linked, fall back to the FG's stored coreCost.
  const derivedCoreCost = coreItemNormalized
    ? numberOrZero(coreItemNormalized.unitCost ?? coreItemNormalized.coreCost)
    : numberOrZero(r?.coreCost);
  return {
    ...r,
    priceLevels: r?.priceLevels && typeof r.priceLevels === "object" ? r.priceLevels : {},
    unitCost: numberOrZero(r?.unitCost),
    unitPrice: numberOrZero(r?.unitPrice),
    coreCost: derivedCoreCost,
    coreCharge: numberOrZero(r?.coreCharge),
    coreItem: coreItemNormalized,
  };
};

const getCoreUnitCost = (item) => {
  const i = item?.attributes ? { id: item.id, documentId: item.documentId || item.id, ...item.attributes } : item;
  const coreRel = i?.coreItem?.data || i?.coreItem || null;
  const core = coreRel?.attributes ? { id: coreRel.id, documentId: coreRel.documentId || coreRel.id, ...coreRel.attributes } : coreRel;
  if (core && typeof core === "object") {
    const raw = core.unitCost ?? core.coreCost;
    if (raw != null && raw !== "") return numberOrZero(raw);
  }
  return numberOrZero(i?.coreCost);
};

const getItemPrice = (item, priceMode, manualPrice) => {
  if (!item) return 0;
  const priceLevels = item.priceLevels && typeof item.priceLevels === "object" ? item.priceLevels : {};
  if (priceMode === "retail") return numberOrZero(priceLevels.retail ?? priceLevels.Retail ?? item.unitPrice);
  if (priceMode === "wholesale") return numberOrZero(priceLevels.wholesale ?? priceLevels.Wholesale ?? item.unitPrice);
  if (priceMode === "bulk") return numberOrZero(priceLevels.bulk ?? priceLevels.Bulk ?? item.unitPrice);
  if (priceMode === "unitPrice") return numberOrZero(item.unitPrice);
  if (priceMode === "manual") return numberOrZero(manualPrice);
  return numberOrZero(item.unitPrice);
};

const sumOperatingExpenseTotalUSD = (expense) => {
  const e = expense?.attributes ? expense.attributes : expense;
  const lines = e?.lines?.data || e?.lines || [];
  if (Array.isArray(lines) && lines.length > 0) {
    return lines.reduce((sum, line) => {
      const l = line?.attributes ? line.attributes : line;
      const qty = numberOrZero(l?.quantity);
      const unitCost = numberOrZero(l?.unitCost);
      const lineTotal = l?.lineTotal != null ? numberOrZero(l.lineTotal) : qty * unitCost;
      return sum + lineTotal;
    }, 0);
  }
  return numberOrZero(e?.total ?? e?.amount);
};

const defaultScenario = () => {
  const now = new Date();
  const start = startOfMonth(now);
  return {
    horizonMonths: 6,
    startMonth: ymKey(start),
    startingCash: 0,
    collectionLagMonths: 1,
    collectionPercent: 100,
    assumeInventoryOnHand: true, // if true, do NOT treat COGS as a cash outflow
    enablePurchasingCashModel: true, // planned component buys drive cash-out
    includeCoreChargeRevenue: true,
    includeCoreCost: true,
    enableCoreRecoup: true,
    coreRecoupPercent: 85,
    coreRecoupLagMonths: 2,
    materialsLeadTimeMonths: 1,
    materialsPaymentTiming: "onOrder", // onOrder | onDelivery
    overrideFixedCostsMonthly: "",
    overrideVariableOpexMonthly: "",
    overridePayrollMonthly: "",
    payrollHoursPerWeek: 40,
    salesLines: [
      {
        id: `line-${Date.now()}`,
        inventoryItem: null,
        priceMode: "unitPrice",
        manualUnitPrice: "",
        quantitiesByMonth: {},
      },
    ],
  };
};

export default function ProjectionsMobile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatUSDForDisplay, mxnRate } = useCurrency();
  const { activeCompany } = useAuth();
  const companyKey = normalizeCompanyKey(activeCompany);
  const companyCurrency = companyKey ? currencyForCompany(companyKey) : "USD";
  const scenarioStorageKey = `vb:projectionsScenario:v2:${companyKey || "no-company"}`;

  const loadScenario = (key) => {
    try {
      const raw = localStorage.getItem(key) ?? localStorage.getItem("vb:projectionsScenario:v1");
      if (!raw) return defaultScenario();
      const parsed = JSON.parse(raw);
      return { ...defaultScenario(), ...parsed };
    } catch (_) {
      return defaultScenario();
    }
  };

  const [scenario, setScenario] = useState(() => loadScenario(scenarioStorageKey));

  useEffect(() => {
    setScenario(loadScenario(scenarioStorageKey));
  }, [scenarioStorageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(scenarioStorageKey, JSON.stringify(scenario));
    } catch (_) {}
  }, [scenario, scenarioStorageKey]);

  const months = useMemo(() => {
    const [y, m] = String(scenario.startMonth).split("-").map((x) => Number(x));
    const start = startOfMonth(new Date(y, (m || 1) - 1, 1));
    return Array.from({ length: Math.max(1, Math.min(24, Number(scenario.horizonMonths) || 6)) }).map((_, idx) =>
      ymKey(addMonths(start, idx))
    );
  }, [scenario.startMonth, scenario.horizonMonths]);

  const scenarioStartDate = useMemo(() => {
    const [y, m] = String(scenario.startMonth).split("-").map((x) => Number(x));
    return startOfMonth(new Date(y, (m || 1) - 1, 1));
  }, [scenario.startMonth]);

  const formatMoney = (usd) => formatUSDForDisplay(numberOrZero(usd));

  const { data: fgItemsData, isLoading: fgLoading } = useQuery({
    queryKey: ["projections-fg-items"],
    queryFn: async () =>
      getAllPages(
        "/api/inventory-items",
        {
          filters: { itemType: { $eq: "FG" } },
          populate: { coreItem: true },
          sort: "name:asc",
          pagination: { pageSize: 100 },
        },
        { pageSize: 200, concurrency: 5 }
      ),
    staleTime: 1000 * 60 * 10,
  });

  const fgItems = useMemo(() => {
    return (fgItemsData?.data || []).map((d) => normalizeInventoryItemForProjection(d)).filter(Boolean);
  }, [fgItemsData]);

  const usedFinishedGoods = useMemo(() => {
    const map = new Map();
    (scenario.salesLines || []).forEach((l) => {
      const item = l?.inventoryItem;
      if (!item) return;
      const key = String(item.documentId || item.id);
      if (!key) return;
      map.set(key, normalizeInventoryItemForProjection(item));
    });
    return [...map.values()].filter(Boolean);
  }, [scenario.salesLines]);

  const fetchInventoryItemForProjection = async (idOrDocId) => {
    if (!idOrDocId) return null;
    const res = await get(`/api/inventory-items/${idOrDocId}`, { populate: { coreItem: true } });
    const entity = res?.data || res;
    return normalizeInventoryItemForProjection(entity);
  };

  // Hydrate selected FG lines to ensure `coreItem` is present even if loaded from localStorage.
  const hydratedFgRef = useRef(new Set());
  useEffect(() => {
    const lines = scenario.salesLines || [];
    if (lines.length === 0) return;

    const missing = lines
      .map((l) => ({ lineId: l.id, item: l.inventoryItem ? normalizeInventoryItemForProjection(l.inventoryItem) : null }))
      .filter(({ item }) => item && String(item.itemType || "").toUpperCase() === "FG")
      .filter(({ item }) => item && item.coreItem == null)
      .filter(({ item }) => {
        const key = String(item.documentId || item.id);
        return key && !hydratedFgRef.current.has(key);
      });

    if (missing.length === 0) return;

    (async () => {
      for (const m of missing) {
        const key = String(m.item.documentId || m.item.id);
        hydratedFgRef.current.add(key);
        try {
          const fresh = await fetchInventoryItemForProjection(m.item.documentId || m.item.id);
          if (fresh) {
            setScenario((prev) => ({
              ...prev,
              salesLines: (prev.salesLines || []).map((l) => (l.id === m.lineId ? { ...l, inventoryItem: fresh } : l)),
            }));
          }
        } catch (_) {
          // ignore
        }
      }
    })();
  }, [scenario.salesLines]);

  const { data: buildSheetsByFgData } = useQuery({
    queryKey: [
      "projections-build-sheets-by-fg",
      usedFinishedGoods.map((i) => i?.documentId || i?.id).filter(Boolean).join("|"),
      scenarioStartDate.toISOString().slice(0, 10),
    ],
    queryFn: async () => {
      const fgIdsRaw = usedFinishedGoods.map((i) => i?.id).filter((x) => x != null).map((x) => String(x));
      const fgDocIdsRaw = usedFinishedGoods.map((i) => i?.documentId).filter((x) => x != null).map((x) => String(x));
      const fgIdKeys = [...new Set(fgIdsRaw)].filter((k) => /^\d+$/.test(k)); // numeric only
      const fgDocKeys = [...new Set(fgDocIdsRaw)].filter((k) => !!k && !/^\d+$/.test(k)); // documentId strings
      if (fgIdKeys.length === 0 && fgDocKeys.length === 0) return {};

      const startISO = scenarioStartDate.toISOString().slice(0, 10);
      const results = await Promise.all(
        [...fgIdKeys.map((k) => ({ kind: "id", key: k })), ...fgDocKeys.map((k) => ({ kind: "doc", key: k }))].map(
          async ({ kind, key }) => {
            try {
              const res = await getAllPages(
                "/api/build-sheets",
                {
                  publicationState: "live",
                  filters: {
                    status: { $eq: "published" },
                    ...(kind === "id"
                      ? { finishedGood: { id: { $eq: Number(key) } } }
                      : { finishedGood: { documentId: { $eq: key } } }),
                  },
                  populate: {
                    finishedGood: true,
                    bulkLines: { populate: { inventoryItem: true } },
                  },
                  sort: "version:desc",
                  pagination: { pageSize: 50 },
                },
                { pageSize: 100, concurrency: 2 }
              );
              const sheets = (res?.data || []).map((d) => normalizeEntity(d)).filter(Boolean);
              const candidates = sheets.filter((s) => !s?.effectiveFrom || String(s.effectiveFrom) <= startISO);
              const best =
                (candidates.length > 0 ? candidates : sheets).sort((a, b) => numberOrZero(b?.version) - numberOrZero(a?.version))[0] ||
                null;
              return [key, best];
            } catch (_) {
              return [key, null];
            }
          }
        )
      );

      const map = {};
      results.forEach(([k, v]) => {
        map[String(k)] = v;
      });
      return map;
    },
    enabled: usedFinishedGoods.length > 0,
    staleTime: 1000 * 60 * 10,
  });

  const buildSheetsByFg = useMemo(() => {
    const m = new Map();
    const raw = buildSheetsByFgData || {};
    Object.entries(raw).forEach(([k, v]) => {
      if (!k) return;
      m.set(String(k), v);
    });
    Object.values(raw).forEach((sheet) => {
      const fg = sheet?.finishedGood?.data || sheet?.finishedGood;
      const fgN = fg ? normalizeEntity(fg) : null;
      const idKey = fgN?.id != null ? String(fgN.id) : null;
      const docKey = fgN?.documentId != null ? String(fgN.documentId) : null;
      if (idKey && !m.has(idKey)) m.set(idKey, sheet);
      if (docKey && !m.has(docKey)) m.set(docKey, sheet);
    });
    return m;
  }, [buildSheetsByFgData]);

  const { data: fixedCostsData } = useQuery({
    queryKey: ["projections-fixed-costs"],
    queryFn: async () =>
      getAllPages(
        "/api/inventory-items",
        {
          filters: { itemType: { $eq: "FIXED_COSTS" } },
          populate: { parentOverhead: true },
          pagination: { pageSize: 100 },
        },
        { pageSize: 200, concurrency: 5 }
      ),
    staleTime: 1000 * 60 * 10,
  });

  const fixedCostsMonthlyDefault = useMemo(() => {
    const items = (fixedCostsData?.data || []).map((d) => normalizeEntity(d));
    return items.reduce((sum, fc) => sum + numberOrZero(fc?.unitCost), 0);
  }, [fixedCostsData]);

  const fixedCostsBreakdown = useMemo(() => {
    const rows = (fixedCostsData?.data || []).map((d) => normalizeEntity(d));
    const list = rows.map((fc) => {
      const parent = fc?.parentOverhead?.data || fc?.parentOverhead;
      const parentN = parent ? normalizeEntity(parent) : null;
      return {
        id: fc.id,
        sku: fc.sku,
        name: fc.name,
        monthly: numberOrZero(fc.unitCost),
        overhead: parentN?.name || parentN?.sku || null,
      };
    });
    list.sort((a, b) => b.monthly - a.monthly);
    return list;
  }, [fixedCostsData]);

  const { data: recentOpexData } = useQuery({
    queryKey: ["projections-operating-expenses-last-3-months"],
    queryFn: async () => {
      const now = new Date();
      const start = startOfMonth(addMonths(now, -3)).toISOString().split("T")[0];
      const end = addMonths(startOfMonth(now), 1).toISOString().split("T")[0];
      return getAllPages(
        "/api/operating-expenses",
        {
          filters: {
            expenseDate: { $gte: start, $lte: end },
            subCategory: { $ne: "payroll" },
          },
          populate: { lines: true, overheadItem: true },
          sort: "expenseDate:desc",
          pagination: { pageSize: 100 },
        },
        { pageSize: 200, concurrency: 5 }
      );
    },
    staleTime: 1000 * 60 * 5,
  });

  const variableOpexBreakdown = useMemo(() => {
    const rows = (recentOpexData?.data || []).map((d) => normalizeEntity(d));
    const map = new Map();
    rows.forEach((e) => {
      const overhead = e?.overheadItem?.data || e?.overheadItem;
      const overheadN = overhead ? normalizeEntity(overhead) : null;
      const overheadName = overheadN?.name || overheadN?.sku || "Unassigned Overhead";
      const sub = (e?.subCategory || "").trim() || "uncategorized";
      const key = `${overheadName} • ${sub}`;
      const total = sumOperatingExpenseTotalUSD(e);
      map.set(key, (map.get(key) || 0) + total);
    });
    const list = [...map.entries()].map(([key, total]) => ({
      key,
      avgMonthly: total / 3,
      last3mo: total,
    }));
    list.sort((a, b) => b.last3mo - a.last3mo);
    return list;
  }, [recentOpexData]);

  const variableOpexMonthlyDefault = useMemo(() => {
    const rows = (recentOpexData?.data || []).map((d) => normalizeEntity(d));
    const total = rows.reduce((sum, e) => sum + sumOperatingExpenseTotalUSD(e), 0);
    return total / 3;
  }, [recentOpexData]);

  const { data: employeesData } = useQuery({
    queryKey: ["projections-employees"],
    queryFn: async () =>
      getAllPages(
        "/api/employees",
        {
          filters: { isActive: { $eq: true } },
          sort: "lastName:asc",
          pagination: { pageSize: 100 },
        },
        { pageSize: 200, concurrency: 5 }
      ),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const payrollEmployeesBreakdown = useMemo(() => {
    const rows = (employeesData?.data || []).map((d) => normalizeEntity(d));
    const hoursPerWeek = clamp(numberOrZero(scenario.payrollHoursPerWeek) || 40, 0, 80);
    const rate = numberOrZero(mxnRate) || 1;
    const weeklyToMonthly = 52 / 12;
    const payPeriodsPerMonth = (freq) => {
      const f = String(freq || "").toLowerCase();
      if (f === "weekly") return weeklyToMonthly;
      if (f === "biweekly") return 26 / 12;
      if (f === "monthly") return 1;
      return 26 / 12;
    };

    return rows
      .map((e) => {
        const payType = String(e?.payType || "hourly").toLowerCase();
        const currency = String(e?.defaultCurrency || "USD").toUpperCase();
        const isMX = String(e?.country || "").toUpperCase() === "MX" || currency === "MXN";
        const freq = isMX ? (e?.mxDefaultPayFrequency || "weekly") : "biweekly";
        let monthly = 0;
        if (payType === "salary") monthly = numberOrZero(e?.salaryPerPayPeriod) * payPeriodsPerMonth(freq);
        else monthly = numberOrZero(e?.hourlyRate) * hoursPerWeek * weeklyToMonthly;
        const monthlyUSD = currency === "MXN" ? monthly / rate : monthly;
        return {
          id: e.id,
          displayName: e.displayName || [e.firstName, e.lastName].filter(Boolean).join(" ") || `employee:${e.id}`,
          country: e.country,
          currency,
          payType,
          hourlyRate: numberOrZero(e.hourlyRate),
          salaryPerPayPeriod: numberOrZero(e.salaryPerPayPeriod),
          assumedFrequency: freq,
          monthlyUSD,
        };
      })
      .sort((a, b) => b.monthlyUSD - a.monthlyUSD);
  }, [employeesData, mxnRate, scenario.payrollHoursPerWeek]);

  const payrollMonthlyDefault = useMemo(() => {
    return payrollEmployeesBreakdown.reduce((sum, e) => sum + numberOrZero(e.monthlyUSD), 0);
  }, [payrollEmployeesBreakdown]);

  const { data: settingsData } = useQuery({
    queryKey: ["projections-global-setting"],
    queryFn: async () =>
      get("/api/global-setting", {
        populate: {
          cashAccount: true,
          bankAccount: true,
          accountsReceivableAccount: true,
          accountsPayableAccount: true,
          inventoryAccount: true,
          salesRevenueAccount: true,
          cogsAccount: true,
          operatingExpenseAccount: true,
        },
      }),
    staleTime: 1000 * 60 * 10,
  });

  const settings = useMemo(() => {
    const d = settingsData?.data || settingsData;
    return normalizeEntity(d);
  }, [settingsData]);

  const fixedCostsMonthly = useMemo(() => {
    if (scenario.overrideFixedCostsMonthly !== "" && scenario.overrideFixedCostsMonthly != null) {
      return numberOrZero(scenario.overrideFixedCostsMonthly);
    }
    return fixedCostsMonthlyDefault;
  }, [scenario.overrideFixedCostsMonthly, fixedCostsMonthlyDefault]);

  const variableOpexMonthly = useMemo(() => {
    if (scenario.overrideVariableOpexMonthly !== "" && scenario.overrideVariableOpexMonthly != null) {
      return numberOrZero(scenario.overrideVariableOpexMonthly);
    }
    return variableOpexMonthlyDefault;
  }, [scenario.overrideVariableOpexMonthly, variableOpexMonthlyDefault]);

  const payrollMonthly = useMemo(() => {
    if (scenario.overridePayrollMonthly !== "" && scenario.overridePayrollMonthly != null) {
      return numberOrZero(scenario.overridePayrollMonthly);
    }
    return payrollMonthlyDefault;
  }, [scenario.overridePayrollMonthly, payrollMonthlyDefault]);

  const materials = useMemo(() => {
    const missingBuildSheets = [];
    const missingCoreItems = [];

    const fgPlan = new Map();
    (scenario.salesLines || []).forEach((l) => {
      const item = l?.inventoryItem ? normalizeInventoryItemForProjection(l.inventoryItem) : null;
      if (!item) return;
      const fgKey = String(item.documentId || item.id);
      if (!fgKey) return;
      const entry = fgPlan.get(fgKey) || { item, unitsByMonth: {} };
      months.forEach((m) => {
        const qty = numberOrZero(l.quantitiesByMonth?.[m]);
        if (qty > 0) entry.unitsByMonth[m] = numberOrZero(entry.unitsByMonth[m]) + qty;
      });
      fgPlan.set(fgKey, entry);
    });

    const normalizeUsageRate = (raw, isOptional) => {
      if (raw == null || raw === "") return isOptional ? 0 : 1;
      const n = numberOrZero(raw);
      if (n <= 0) return 0;
      if (n > 1 && n <= 100) return clamp(n / 100, 0, 1);
      return clamp(n, 0, 1);
    };

    const demandByParts = new Map();
    const demandByCores = new Map();
    fgPlan.forEach((plan, fgKey) => {
      const sheet = buildSheetsByFg.get(String(fgKey));
      if (!sheet) {
        const fgDisplay = plan?.item?.sku || plan?.item?.name || fgKey;
        missingBuildSheets.push(fgDisplay);
        return;
      }
      const bulkLines = Array.isArray(sheet?.bulkLines) ? sheet.bulkLines : [];
      bulkLines.forEach((bl) => {
        const blData = bl?.attributes ? bl.attributes : bl;
        const inv = blData?.inventoryItem?.data || blData?.inventoryItem;
        const invN = inv ? normalizeInventoryItemForProjection(inv) : null;
        const compKey = invN ? String(invN.documentId || invN.id) : null;
        if (!compKey) return;

        const perUnitQty = numberOrZero(blData?.quantity);
        if (perUnitQty <= 0) return;
        const isOptional = !!blData?.isOptional;
        const usageRate = normalizeUsageRate(blData?.usageRate, isOptional);
        if (usageRate <= 0) return;

        const isCore = String(invN?.itemType || "").toUpperCase() === "CORE";
        const target = isCore ? demandByCores : demandByParts;
        const entry = target.get(compKey) || { item: invN, demandByMonth: {}, totalDemand: 0 };
        entry.item = invN || entry.item;
        months.forEach((m) => {
          const fgUnits = numberOrZero(plan?.unitsByMonth?.[m]);
          if (fgUnits <= 0) return;
          const demand = fgUnits * perUnitQty * usageRate;
          entry.demandByMonth[m] = numberOrZero(entry.demandByMonth[m]) + demand;
          entry.totalDemand += demand;
        });
        target.set(compKey, entry);
      });

      const fgCoreItem = plan?.item?.coreItem ? normalizeInventoryItemForProjection(plan.item.coreItem) : null;
      if (!fgCoreItem) {
        const fgDisplay = plan?.item?.sku || plan?.item?.name || fgKey;
        if (fgDisplay && !missingCoreItems.includes(fgDisplay)) missingCoreItems.push(fgDisplay);
        return;
      }
      const coreKey = String(fgCoreItem.documentId || fgCoreItem.id);
      if (!coreKey) return;
      const coreEntry = demandByCores.get(coreKey) || { item: fgCoreItem, demandByMonth: {}, totalDemand: 0 };
      coreEntry.item = fgCoreItem || coreEntry.item;
      months.forEach((m) => {
        const fgUnits = numberOrZero(plan?.unitsByMonth?.[m]);
        if (fgUnits <= 0) return;
        const demand = fgUnits * 1;
        coreEntry.demandByMonth[m] = numberOrZero(coreEntry.demandByMonth[m]) + demand;
        coreEntry.totalDemand += demand;
      });
      demandByCores.set(coreKey, coreEntry);
    });

    const buildCoverageRows = (demandMap) => {
      return [...demandMap.entries()].map(([itemKey, entry]) => {
        const item = entry.item || {};
        const onHand = numberOrZero(item.quantityOnHand);
        const reserved = numberOrZero(item.quantityReserved);
        const availableStart = onHand - reserved;

        let balance = availableStart;
        let minBalance = balance;
        let minMonth = months[0] || null;

        months.forEach((m) => {
          balance -= numberOrZero(entry.demandByMonth?.[m]);
          if (balance < minBalance) {
            minBalance = balance;
            minMonth = m;
          }
        });

        const additionalUnitsNeeded = Math.max(0, -minBalance);
        const unitCost = numberOrZero(item.unitCost);
        const shortfallCash = additionalUnitsNeeded * unitCost;

        return {
          id: itemKey,
          itemType: item.itemType,
          sku: item.sku,
          name: item.name,
          onHand,
          reserved,
          availableStart,
          plannedDemandTotal: numberOrZero(entry.totalDemand),
          minBalance,
          minMonth,
          additionalUnitsNeeded,
          unitCost,
          shortfallCash,
        };
      });
    };

    const partsCoverageRows = buildCoverageRows(demandByParts);
    const coreCoverageRows = buildCoverageRows(demandByCores);

    partsCoverageRows.sort((a, b) => numberOrZero(b.shortfallCash) - numberOrZero(a.shortfallCash));
    coreCoverageRows.sort((a, b) => numberOrZero(b.shortfallCash) - numberOrZero(a.shortfallCash));

    const partsShortfallCashTotal = partsCoverageRows.reduce((sum, r) => sum + numberOrZero(r.shortfallCash), 0);
    const coreShortfallCashTotal = coreCoverageRows.reduce((sum, r) => sum + numberOrZero(r.shortfallCash), 0);

    return {
      missingBuildSheets,
      missingCoreItems,
      partsCoverageRows,
      coreCoverageRows,
      partsShortfallCashTotal,
      coreShortfallCashTotal,
      shortfallCashTotal: partsShortfallCashTotal + coreShortfallCashTotal,
    };
  }, [scenario.salesLines, months, buildSheetsByFg]);

  const projectedByMonth = useMemo(() => {
    const map = new Map();
    months.forEach((m) => {
      map.set(m, {
        month: m,
        revenue: 0,
        cogs: 0,
        coreCost: 0,
        opex: fixedCostsMonthly + variableOpexMonthly + payrollMonthly,
      });
    });

    (scenario.salesLines || []).forEach((line) => {
      const item = line.inventoryItem ? normalizeInventoryItemForProjection(line.inventoryItem) : null;
      if (!item) return;
      months.forEach((m) => {
        const qty = numberOrZero(line.quantitiesByMonth?.[m]);
        if (qty <= 0) return;
        const unitPrice = getItemPrice(item, line.priceMode, line.manualUnitPrice);
        const coreCharge = scenario.includeCoreChargeRevenue ? numberOrZero(item.coreCharge) : 0;
        const revenue = qty * (unitPrice + coreCharge);
        const cogs = qty * numberOrZero(item.unitCost);
        const coreCost = scenario.includeCoreCost ? qty * getCoreUnitCost(item) : 0;
        const row = map.get(m);
        row.revenue += revenue;
        row.cogs += cogs;
        row.coreCost += coreCost;
      });
    });

    const collectionLag = Math.max(0, Math.min(6, Number(scenario.collectionLagMonths) || 0));
    const collectionPct = Math.max(0, Math.min(100, Number(scenario.collectionPercent) || 100)) / 100;
    const treatCogsAsCash = !scenario.assumeInventoryOnHand && !scenario.enablePurchasingCashModel;
    const enableCoreRecoup = !!scenario.enableCoreRecoup;
    const coreRecoupPct = clamp(numberOrZero(scenario.coreRecoupPercent) / 100, 0, 1);
    const coreRecoupLag = clamp(Math.floor(numberOrZero(scenario.coreRecoupLagMonths)), 0, 6);

    const monthsArr = [...months];
    const coreRecoupByMonth = new Map();
    monthsArr.forEach((m) => coreRecoupByMonth.set(m, 0));
    if (scenario.includeCoreCost && enableCoreRecoup && coreRecoupPct > 0) {
      monthsArr.forEach((m, idx) => {
        const source = map.get(m);
        const recoup = numberOrZero(source?.coreCost) * coreRecoupPct;
        const targetIdx = idx + coreRecoupLag;
        if (recoup > 0 && targetIdx >= 0 && targetIdx < monthsArr.length) {
          const targetMonth = monthsArr[targetIdx];
          coreRecoupByMonth.set(targetMonth, numberOrZero(coreRecoupByMonth.get(targetMonth)) + recoup);
        }
      });
    }

    const plannedPartsPurchaseCashOutByMonth = new Map();
    const plannedCorePurchaseCashOutByMonth = new Map();
    monthsArr.forEach((m) => {
      plannedPartsPurchaseCashOutByMonth.set(m, 0);
      plannedCorePurchaseCashOutByMonth.set(m, 0);
    });
    if (scenario.enablePurchasingCashModel) {
      const leadMonths = clamp(Math.floor(numberOrZero(scenario.materialsLeadTimeMonths)), 0, 12);
      const payTiming = String(scenario.materialsPaymentTiming || "onOrder");

      const schedule = (row, targetMap) => {
        const needUnits = numberOrZero(row.additionalUnitsNeeded);
        if (needUnits <= 0) return;
        const needMonth = row.minMonth || monthsArr[monthsArr.length - 1];
        let payMonth = needMonth;
        if (payTiming === "onOrder") {
          const idx = Math.max(0, monthsArr.indexOf(needMonth) - leadMonths);
          payMonth = monthsArr[idx] || monthsArr[0];
        }
        if (!monthsArr.includes(payMonth)) payMonth = monthsArr[0];
        const cost = needUnits * numberOrZero(row.unitCost);
        targetMap.set(payMonth, numberOrZero(targetMap.get(payMonth)) + cost);
      };

      (materials?.partsCoverageRows || []).forEach((r) => schedule(r, plannedPartsPurchaseCashOutByMonth));
      (materials?.coreCoverageRows || []).forEach((r) => schedule(r, plannedCorePurchaseCashOutByMonth));
    }

    const byMonth = monthsArr.map((m, idx) => {
      const base = map.get(m);
      const grossProfit = base.revenue - base.cogs - base.coreCost;
      const netProfit = grossProfit - base.opex;

      const cashInSourceIdx = idx - collectionLag;
      const cashInAR = cashInSourceIdx >= 0 ? map.get(monthsArr[cashInSourceIdx]).revenue * collectionPct : 0;
      const cashInCoreRecoup = numberOrZero(coreRecoupByMonth.get(m));
      const cashIn = cashInAR + cashInCoreRecoup;

      const plannedPartsPurchaseCashOut = scenario.enablePurchasingCashModel ? numberOrZero(plannedPartsPurchaseCashOutByMonth.get(m)) : 0;
      const plannedCorePurchaseCashOut = scenario.enablePurchasingCashModel ? numberOrZero(plannedCorePurchaseCashOutByMonth.get(m)) : 0;
      const purchasingCashOut = scenario.enablePurchasingCashModel ? plannedPartsPurchaseCashOut + plannedCorePurchaseCashOut : 0;
      const cashOut = base.opex + (treatCogsAsCash ? base.cogs + base.coreCost : 0) + purchasingCashOut;
      const netCash = cashIn - cashOut;
      const capitalNeededStartOfMonth = cashOut;

      return {
        ...base,
        grossProfit,
        netProfit,
        cashInAR,
        cashInCoreRecoup,
        cashIn,
        plannedPartsPurchaseCashOut,
        plannedCorePurchaseCashOut,
        purchasingCashOut,
        cashOut,
        netCash,
        capitalNeededStartOfMonth,
      };
    });

    let cash = numberOrZero(scenario.startingCash);
    return byMonth.map((r) => {
      cash += numberOrZero(r.netCash);
      return { ...r, endingCash: cash };
    });
  }, [
    months,
    scenario.salesLines,
    scenario.includeCoreChargeRevenue,
    scenario.includeCoreCost,
    scenario.collectionLagMonths,
    scenario.collectionPercent,
    scenario.assumeInventoryOnHand,
    scenario.enablePurchasingCashModel,
    scenario.materialsLeadTimeMonths,
    scenario.materialsPaymentTiming,
    scenario.startingCash,
    scenario.enableCoreRecoup,
    scenario.coreRecoupPercent,
    scenario.coreRecoupLagMonths,
    mxnRate,
    materials,
    fixedCostsMonthly,
    variableOpexMonthly,
    payrollMonthly,
  ]);

  const totals = useMemo(() => {
    return projectedByMonth.reduce(
      (acc, r) => {
        acc.revenue += r.revenue;
        acc.cogs += r.cogs;
        acc.coreCost += r.coreCost;
        acc.opex += r.opex;
        acc.cashIn += r.cashIn;
        acc.cashInAR += numberOrZero(r.cashInAR);
        acc.cashInCoreRecoup += numberOrZero(r.cashInCoreRecoup);
        acc.plannedPartsPurchaseCashOut += numberOrZero(r.plannedPartsPurchaseCashOut);
        acc.plannedCorePurchaseCashOut += numberOrZero(r.plannedCorePurchaseCashOut);
        acc.purchasingCashOut += numberOrZero(r.purchasingCashOut);
        acc.cashOut += r.cashOut;
        acc.netCash += r.netCash;
        return acc;
      },
      {
        revenue: 0,
        cogs: 0,
        coreCost: 0,
        opex: 0,
        cashIn: 0,
        cashInAR: 0,
        cashInCoreRecoup: 0,
        plannedPartsPurchaseCashOut: 0,
        plannedCorePurchaseCashOut: 0,
        purchasingCashOut: 0,
        cashOut: 0,
        netCash: 0,
      }
    );
  }, [projectedByMonth]);

  const cashflow = useMemo(() => {
    let maxCashOut = 0;
    let maxCashOutMonth = null;
    let maxCashOutRow = null;

    projectedByMonth.forEach((r) => {
      const co = numberOrZero(r.cashOut);
      if (co > maxCashOut) {
        maxCashOut = co;
        maxCashOutMonth = r.month;
        maxCashOutRow = r;
      }
    });

    return {
      maxCashOut,
      maxCashOutMonth,
      maxCashOutRow,
      totalPurchasingCashOut: numberOrZero(totals.purchasingCashOut),
    };
  }, [projectedByMonth, totals.purchasingCashOut]);

  const insights = useMemo(() => {
    let minEndingCash = Number.POSITIVE_INFINITY;
    let minEndingCashMonth = null;
    let minEndingCashIdx = -1;

    let cumulativeNetProfit = 0;
    let profitBreakevenMonth = null;
    let profitBreakevenMonthIdx = -1;

    let cashBreakevenMonth = null;
    let cashBreakevenMonthIdx = -1;

    projectedByMonth.forEach((r, idx) => {
      const endingCash = numberOrZero(r.endingCash);
      if (endingCash < minEndingCash) {
        minEndingCash = endingCash;
        minEndingCashMonth = r.month;
        minEndingCashIdx = idx;
      }

      cumulativeNetProfit += numberOrZero(r.netProfit);
      if (profitBreakevenMonth == null && cumulativeNetProfit >= 0) {
        profitBreakevenMonth = r.month;
        profitBreakevenMonthIdx = idx;
      }

      if (cashBreakevenMonth == null && endingCash >= 0) {
        cashBreakevenMonth = r.month;
        cashBreakevenMonthIdx = idx;
      }
    });

    const peakAdditionalCapitalNeeded = Math.max(0, -numberOrZero(minEndingCash));

    const demandByItem = new Map();
    (scenario.salesLines || []).forEach((l) => {
      const itemRaw = l?.inventoryItem;
      const item = itemRaw ? normalizeInventoryItemForProjection(itemRaw) : null;
      if (!item) return;
      const key = String(item.documentId || item.id);
      if (!key) return;
      const prev = demandByItem.get(key) || { item, totalUnits: 0 };
      const lineUnits = months.reduce((sum, m) => sum + numberOrZero(l.quantitiesByMonth?.[m]), 0);
      prev.totalUnits += lineUnits;
      prev.item = item;
      demandByItem.set(key, prev);
    });

    const inventoryCoverageRows = [...demandByItem.values()]
      .map(({ item, totalUnits }) => {
        const onHand = numberOrZero(item.quantityOnHand);
        const reserved = numberOrZero(item.quantityReserved);
        const available = onHand - reserved;
        const shortUnits = Math.max(0, totalUnits - available);
        const unitPurchaseCost = numberOrZero(item.unitCost) + (scenario.includeCoreCost ? getCoreUnitCost(item) : 0);
        const shortfallCash = shortUnits * unitPurchaseCost;
        return {
          id: item.id || item.documentId,
          sku: item.sku,
          name: item.name,
          onHand,
          reserved,
          available,
          plannedUnits: totalUnits,
          shortUnits,
          shortfallCash,
        };
      })
      .sort((a, b) => b.shortfallCash - a.shortfallCash);

    const inventoryShortfallCashTotal = inventoryCoverageRows.reduce((sum, r) => sum + numberOrZero(r.shortfallCash), 0);

    return {
      minEndingCash: Number.isFinite(minEndingCash) ? minEndingCash : 0,
      minEndingCashMonth,
      minEndingCashIdx,
      peakAdditionalCapitalNeeded,
      profitBreakevenMonth,
      profitBreakevenMonthIdx,
      cashBreakevenMonth,
      cashBreakevenMonthIdx,
      inventoryCoverageRows,
      inventoryShortfallCashTotal,
    };
  }, [projectedByMonth, scenario.salesLines, scenario.includeCoreCost, months]);

  const addSalesLine = () => {
    setScenario((prev) => ({
      ...prev,
      salesLines: [
        ...(prev.salesLines || []),
        { id: `line-${Date.now()}-${Math.random().toString(16).slice(2)}`, inventoryItem: null, priceMode: "unitPrice", manualUnitPrice: "", quantitiesByMonth: {} },
      ],
    }));
  };

  const removeSalesLine = (id) => {
    setScenario((prev) => ({ ...prev, salesLines: (prev.salesLines || []).filter((l) => l.id !== id) }));
  };

  const resetScenario = () => setScenario(defaultScenario());

  const setLine = (lineId, patch) => {
    setScenario((prev) => ({
      ...prev,
      salesLines: (prev.salesLines || []).map((l) => (l.id === lineId ? { ...l, ...patch } : l)),
    }));
  };

  const setLineQty = (lineId, month, qty) => {
    setScenario((prev) => ({
      ...prev,
      salesLines: (prev.salesLines || []).map((l) => {
        if (l.id !== lineId) return l;
        return {
          ...l,
          quantitiesByMonth: { ...(l.quantitiesByMonth || {}), [month]: qty },
        };
      }),
    }));
  };

  const handleDownloadPdf = () => {
    try {
      const companyName = (settings?.companyName || "Valve Body Specialties").toString();
      const generatedAt = new Date();
      const filenameDate = generatedAt.toISOString().slice(0, 10);
      const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 32;
      const contentW = pageW - margin * 2;

      const fmt = (n) => formatUSDForDisplay(numberOrZero(n));
      const asPct = (n) => `${clamp(numberOrZero(n), 0, 100).toFixed(0)}%`;

      let y = margin;
      const ensureSpace = (needed) => {
        if (y + needed <= pageH - margin) return;
        doc.addPage();
        y = margin;
      };

      const addH1 = (text) => {
        ensureSpace(34);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(text, margin, y);
        y += 20;
      };

      const addH2 = (text) => {
        ensureSpace(28);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(text, margin, y);
        y += 14;
        doc.setDrawColor(0);
        doc.setLineWidth(0.5);
        doc.line(margin, y, margin + contentW, y);
        y += 10;
      };

      const addParagraph = (text, fontSize = 10) => {
        const lineH = Math.round(fontSize * 1.35);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(fontSize);
        const lines = doc.splitTextToSize(String(text || ""), contentW);
        ensureSpace(lines.length * lineH + 6);
        lines.forEach((ln) => {
          doc.text(ln, margin, y);
          y += lineH;
        });
        y += 6;
      };

      const addKeyValues = (rows) => {
        const fontSize = 9;
        const lineH = 13;
        const labelW = 240;
        const valueW = contentW - labelW;
        doc.setFontSize(fontSize);
        rows.forEach(([label, value]) => {
          const l = String(label ?? "");
          const v = String(value ?? "");
          const vLines = doc.splitTextToSize(v, valueW);
          ensureSpace(Math.max(lineH, vLines.length * lineH) + 2);
          doc.setFont("helvetica", "bold");
          doc.text(l, margin, y);
          doc.setFont("helvetica", "normal");
          vLines.forEach((ln, idx) => {
            doc.text(ln, margin + labelW, y + idx * lineH);
          });
          y += Math.max(lineH, vLines.length * lineH) + 2;
        });
        y += 8;
      };

      const drawSimpleTable = ({ title, columns, rows }) => {
        addH2(title);
        const fontSize = 8;
        const rowH = 14;
        doc.setFontSize(fontSize);

        const colCount = columns.length;
        const colW = contentW / Math.max(1, colCount);

        // Header
        ensureSpace(rowH + 6);
        doc.setFont("helvetica", "bold");
        columns.forEach((c, idx) => {
          doc.text(String(c ?? ""), margin + idx * colW + 2, y);
        });
        y += rowH;
        doc.setFont("helvetica", "normal");
        doc.setDrawColor(0);
        doc.setLineWidth(0.25);
        doc.line(margin, y - 8, margin + contentW, y - 8);

        rows.forEach((r) => {
          ensureSpace(rowH + 2);
          r.forEach((cell, idx) => {
            const t = String(cell ?? "");
            const truncated = t.length > 60 ? `${t.slice(0, 57)}...` : t;
            doc.text(truncated, margin + idx * colW + 2, y);
          });
          y += rowH;
        });
        y += 8;
      };

      addH1(`${companyName} — Projections`);
      addParagraph(`Generated: ${generatedAt.toLocaleString()}`);

      addH2("Scenario");
      addKeyValues([
        ["Horizon", `${scenario.horizonMonths} months starting ${scenario.startMonth}`],
        ["Starting cash", fmt(scenario.startingCash)],
        ["Collection", `${scenario.collectionLagMonths} mo lag • ${asPct(scenario.collectionPercent)} collected`],
        ["Inventory assumption", scenario.assumeInventoryOnHand ? "Assume on hand (COGS not cash-out)" : "Do not assume on hand"],
        ["Purchasing cash model", scenario.enablePurchasingCashModel ? "ON (planned buys drive cash-out)" : "OFF"],
        ["Core charge revenue", scenario.includeCoreChargeRevenue ? "Included" : "Excluded"],
        ["Core cost", scenario.includeCoreCost ? "Included" : "Excluded"],
        ["Core recoup", scenario.enableCoreRecoup ? `${asPct(scenario.coreRecoupPercent)} lag ${scenario.coreRecoupLagMonths} mo` : "Off"],
        ["Materials lead time", `${scenario.materialsLeadTimeMonths} mo`],
        ["Materials payment", String(scenario.materialsPaymentTiming || "onOrder")],
        ["Fixed costs / mo", fmt(fixedCostsMonthly)],
        ["Variable OpEx / mo", fmt(variableOpexMonthly)],
        ["Payroll / mo", fmt(payrollMonthly)],
      ]);

      addH2("Summary");
      addKeyValues([
        ["Total revenue", fmt(totals.revenue)],
        ["Total COGS (FG)", fmt(totals.cogs)],
        ["Total core cost", fmt(totals.coreCost)],
        ["Total OpEx", fmt(totals.opex)],
        ["Peak additional capital needed", fmt(insights.peakAdditionalCapitalNeeded)],
        ["Peak month cash-out", cashflow.maxCashOutMonth ? `${monthLabel(cashflow.maxCashOutMonth)} • ${fmt(cashflow.maxCashOut)}` : fmt(cashflow.maxCashOut)],
      ]);

      const cashPlanRows = (projectedByMonth || []).map((r) => [
        monthLabel(r.month),
        fmt(r.opex),
        fmt(r.plannedPartsPurchaseCashOut),
        fmt(r.plannedCorePurchaseCashOut),
        fmt(r.cashIn),
        fmt(r.cashOut),
        fmt(r.netCash),
        fmt(r.endingCash),
      ]);
      drawSimpleTable({
        title: "Cash plan (month-by-month)",
        columns: ["Month", "OpEx", "Parts buy", "Cores buy", "Cash-in", "Cash-out", "Net cash", "End cash"],
        rows: cashPlanRows.length > 0 ? cashPlanRows : [["", "", "", "", "", "", "", ""]],
      });

      const topVar = (variableOpexBreakdown || []).slice(0, 12).map((v) => [v.key, fmt(v.avgMonthly), fmt(v.last3mo)]);
      drawSimpleTable({
        title: "Variable OpEx (top categories)",
        columns: ["Category", "Avg / month", "Last 3 months total"],
        rows: topVar.length > 0 ? topVar : [["", "No operating expenses found", fmt(0)]],
      });

      const topPayroll = (payrollEmployeesBreakdown || []).slice(0, 12).map((e) => [
        e.displayName || "",
        (e.currency || "") + (e.country ? ` (${e.country})` : ""),
        e.payType || "",
        fmt(e.monthlyUSD),
      ]);
      drawSimpleTable({
        title: "Payroll estimate (monthly) — employees",
        columns: ["Employee", "Currency", "Pay type", `Monthly (${companyCurrency})`],
        rows: topPayroll.length > 0 ? topPayroll : [["", "No employees found (or none active)", "", fmt(0)]],
      });

      doc.save(`projections-${filenameDate}.pdf`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Unable to generate PDF: ${e?.message || e}`);
    }
  };

  const refreshData = () => {
    queryClient.invalidateQueries({ queryKey: ["projections-fg-items"] });
    queryClient.invalidateQueries({ queryKey: ["projections-employees"] });
    queryClient.invalidateQueries({ queryKey: ["projections-fixed-costs"] });
    queryClient.invalidateQueries({ queryKey: ["projections-operating-expenses-last-3-months"] });
    queryClient.invalidateQueries({ queryKey: ["projections-global-setting"] });
    queryClient.invalidateQueries({ queryKey: ["projections-build-sheets-by-fg"] });
  };

  return (
    <Box sx={{ px: 1, pb: 2 }}>
      <MobileTopBar
        title="Projections"
        subtitle="Mobile (full)"
        onBack={() => navigate("/m/dashboard")}
        right={
          <Stack direction="row" spacing={0.25}>
            <IconButton aria-label="Export PDF" size="small" onClick={handleDownloadPdf}>
              <Icon fontSize="small">picture_as_pdf</Icon>
            </IconButton>
            <IconButton aria-label="Refresh data" size="small" onClick={refreshData}>
              <Icon fontSize="small">refresh</Icon>
            </IconButton>
            <IconButton aria-label="Reset scenario" size="small" onClick={resetScenario}>
              <Icon fontSize="small">restart_alt</Icon>
            </IconButton>
          </Stack>
        }
      />

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {!scenario.enablePurchasingCashModel ? (
          <Alert severity="warning" variant="outlined">
            Purchasing cash model is OFF. Capital needed will not include planned material purchases. Turn it on in Assumptions for more realistic cash needs.
          </Alert>
        ) : (
          <Alert severity="info" variant="outlined">
            Purchasing cash model is ON. Cash-out includes OpEx + planned material buys to cover component shortages.
          </Alert>
        )}

        <Stack direction="row" spacing={0.75} sx={{ overflowX: "auto", pb: 0.25 }}>
          <MetricPill label="Peak capital" value={formatMoney(insights.peakAdditionalCapitalNeeded)} />
          <MetricPill
            label="Peak month cash-out"
            value={cashflow.maxCashOutMonth ? `${formatMoney(cashflow.maxCashOut)} (${monthLabel(cashflow.maxCashOutMonth)})` : formatMoney(cashflow.maxCashOut)}
          />
          <MetricPill label="Materials shortfall" value={formatMoney(materials.shortfallCashTotal)} />
        </Stack>

        {numberOrZero(materials.shortfallCashTotal) > 0 ? (
          <Alert severity="warning" variant="outlined">
            You are short components needed to build the planned volume. Estimated material cash shortfall: <strong>{formatMoney(materials.shortfallCashTotal)}</strong>.
          </Alert>
        ) : null}

        {insights.inventoryShortfallCashTotal > 0 ? (
          <Alert severity="warning" variant="outlined">
            Planned unit sales exceed current available FG stock for at least one item. Inventory shortfall cash:{" "}
            <strong>{formatMoney(insights.inventoryShortfallCashTotal)}</strong>.
          </Alert>
        ) : null}

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight={900}>
              Assumptions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={0.75}>
                <TextField
                  label="Horizon (months)"
                  type="number"
                  value={scenario.horizonMonths}
                  onChange={(e) => setScenario((p) => ({ ...p, horizonMonths: Number(e.target.value || 6) }))}
                  inputProps={{ min: 1, max: 24 }}
                  fullWidth
                />
                <TextField
                  label="Start month"
                  value={scenario.startMonth}
                  onChange={(e) => setScenario((p) => ({ ...p, startMonth: e.target.value }))}
                  placeholder="2026-01"
                  fullWidth
                />
              </Stack>
              <TextField
                label={`Starting cash (${companyCurrency})`}
                type="number"
                value={scenario.startingCash}
                onChange={(e) => setScenario((p) => ({ ...p, startingCash: e.target.value }))}
                fullWidth
              />
              <Stack direction="row" spacing={0.75}>
                <TextField
                  label="Collection lag (months)"
                  type="number"
                  value={scenario.collectionLagMonths}
                  onChange={(e) => setScenario((p) => ({ ...p, collectionLagMonths: Number(e.target.value || 0) }))}
                  inputProps={{ min: 0, max: 6 }}
                  fullWidth
                />
                <TextField
                  label="Collection %"
                  type="number"
                  value={scenario.collectionPercent}
                  onChange={(e) => setScenario((p) => ({ ...p, collectionPercent: Number(e.target.value || 100) }))}
                  inputProps={{ min: 0, max: 100 }}
                  fullWidth
                />
              </Stack>

              <FormControlLabel
                control={<Switch checked={!!scenario.assumeInventoryOnHand} onChange={(e) => setScenario((p) => ({ ...p, assumeInventoryOnHand: e.target.checked }))} />}
                label="Assume inventory already on hand (COGS not cash-out)"
              />
              <FormControlLabel
                control={
                  <Switch checked={!!scenario.enablePurchasingCashModel} onChange={(e) => setScenario((p) => ({ ...p, enablePurchasingCashModel: e.target.checked }))} />
                }
                label="Use purchasing cash model (planned component buys)"
              />

              <Stack direction="row" spacing={0.75}>
                <TextField
                  label="Materials lead time (months)"
                  type="number"
                  value={scenario.materialsLeadTimeMonths}
                  onChange={(e) => setScenario((p) => ({ ...p, materialsLeadTimeMonths: Number(e.target.value || 0) }))}
                  inputProps={{ min: 0, max: 12 }}
                  fullWidth
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Materials payment</InputLabel>
                  <Select
                    value={scenario.materialsPaymentTiming || "onOrder"}
                    label="Materials payment"
                    onChange={(e) => setScenario((p) => ({ ...p, materialsPaymentTiming: e.target.value }))}
                  >
                    <MenuItem value="onOrder">Pay on order</MenuItem>
                    <MenuItem value="onDelivery">Pay on delivery</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <FormControlLabel
                control={
                  <Switch checked={!!scenario.includeCoreChargeRevenue} onChange={(e) => setScenario((p) => ({ ...p, includeCoreChargeRevenue: e.target.checked }))} />
                }
                label="Include core charge in revenue"
              />
              <FormControlLabel
                control={<Switch checked={!!scenario.includeCoreCost} onChange={(e) => setScenario((p) => ({ ...p, includeCoreCost: e.target.checked }))} />}
                label="Include core cost"
              />
              <FormControlLabel
                control={<Switch checked={!!scenario.enableCoreRecoup} onChange={(e) => setScenario((p) => ({ ...p, enableCoreRecoup: e.target.checked }))} />}
                label="Enable core recoup"
              />

              <Stack direction="row" spacing={0.75}>
                <TextField
                  label="Core recoup %"
                  type="number"
                  value={scenario.coreRecoupPercent}
                  onChange={(e) => setScenario((p) => ({ ...p, coreRecoupPercent: Number(e.target.value || 0) }))}
                  inputProps={{ min: 0, max: 100 }}
                  fullWidth
                />
                <TextField
                  label="Core recoup lag (months)"
                  type="number"
                  value={scenario.coreRecoupLagMonths}
                  onChange={(e) => setScenario((p) => ({ ...p, coreRecoupLagMonths: Number(e.target.value || 0) }))}
                  inputProps={{ min: 0, max: 6 }}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Payroll hours/week (for hourly employees)"
                type="number"
                value={scenario.payrollHoursPerWeek}
                onChange={(e) => setScenario((p) => ({ ...p, payrollHoursPerWeek: Number(e.target.value || 0) }))}
                inputProps={{ min: 0, max: 80 }}
                fullWidth
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight={900}>
              Monthly cost baseline
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.75}>
              <Card variant="outlined">
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" fontWeight={900}>
                    Fixed costs (default)
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {formatMoney(fixedCostsMonthlyDefault)}
                  </Typography>
                  <TextField
                    sx={{ mt: 0.75 }}
                    label={`Override fixed costs / month (${companyCurrency})`}
                    type="number"
                    value={scenario.overrideFixedCostsMonthly}
                    onChange={(e) => setScenario((p) => ({ ...p, overrideFixedCostsMonthly: e.target.value }))}
                    placeholder={String(fixedCostsMonthlyDefault.toFixed(2))}
                    fullWidth
                  />
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    Breakdown (monthly)
                  </Typography>
                  <Stack spacing={0.5}>
                    {fixedCostsBreakdown.slice(0, 10).map((fc) => (
                      <Stack key={`fc-${fc.id}`} direction="row" justifyContent="space-between" spacing={0.75}>
                        <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                          {fc.name || fc.sku || `#${fc.id}`}
                        </Typography>
                        <Typography variant="caption" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
                          {formatMoney(fc.monthly)}
                        </Typography>
                      </Stack>
                    ))}
                    {fixedCostsBreakdown.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No fixed costs found (itemType = FIXED_COSTS)
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Card>

              <Card variant="outlined">
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" fontWeight={900}>
                    Variable OpEx (avg monthly)
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {formatMoney(variableOpexMonthlyDefault)}
                  </Typography>
                  <TextField
                    sx={{ mt: 0.75 }}
                    label={`Override variable OpEx / month (${companyCurrency})`}
                    type="number"
                    value={scenario.overrideVariableOpexMonthly}
                    onChange={(e) => setScenario((p) => ({ ...p, overrideVariableOpexMonthly: e.target.value }))}
                    placeholder={String(variableOpexMonthlyDefault.toFixed(2))}
                    fullWidth
                  />
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    Top categories (avg monthly)
                  </Typography>
                  <Stack spacing={0.5}>
                    {variableOpexBreakdown.slice(0, 10).map((v) => (
                      <Stack key={v.key} direction="row" justifyContent="space-between" spacing={0.75}>
                        <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                          {v.key}
                        </Typography>
                        <Typography variant="caption" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
                          {formatMoney(v.avgMonthly)}
                        </Typography>
                      </Stack>
                    ))}
                    {variableOpexBreakdown.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No operating expenses found (last 3 months)
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Card>

              <Card variant="outlined">
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" fontWeight={900}>
                    Payroll (monthly, employee-estimated)
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {formatMoney(payrollMonthlyDefault)}
                  </Typography>
                  <TextField
                    sx={{ mt: 0.75 }}
                    label={`Override payroll / month (${companyCurrency})`}
                    type="number"
                    value={scenario.overridePayrollMonthly}
                    onChange={(e) => setScenario((p) => ({ ...p, overridePayrollMonthly: e.target.value }))}
                    placeholder={String(payrollMonthlyDefault.toFixed(2))}
                    fullWidth
                  />
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    Employees (top 10)
                  </Typography>
                  <Stack spacing={0.5}>
                    {payrollEmployeesBreakdown.slice(0, 10).map((e) => (
                      <Stack key={`emp-${e.id}`} direction="row" justifyContent="space-between" spacing={0.75}>
                        <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                          {e.displayName}
                        </Typography>
                        <Typography variant="caption" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
                          {formatMoney(e.monthlyUSD)}
                        </Typography>
                      </Stack>
                    ))}
                    {payrollEmployeesBreakdown.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No active employees found.
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Card>

              <Card variant="outlined">
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" fontWeight={900}>
                    Total OpEx per month
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {formatMoney(fixedCostsMonthly + variableOpexMonthly + payrollMonthly)}
                  </Typography>
                  {settings?.operatingExpenseAccount ? (
                    <Typography variant="caption" color="text.secondary">
                      Posting account (settings): {settings.operatingExpenseAccount?.name || "Operating Expense"}
                    </Typography>
                  ) : null}
                </Box>
              </Card>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={900}>
                Sales plan
              </Typography>
              <Chip size="small" variant="outlined" label={`${(scenario.salesLines || []).length} lines`} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.75}>
              <Button variant="contained" onClick={addSalesLine} startIcon={<Icon>add</Icon>}>
                Add line
              </Button>

              {(scenario.salesLines || []).map((line) => {
                const item = line.inventoryItem ? normalizeInventoryItemForProjection(line.inventoryItem) : null;
                const unitPrice = getItemPrice(item, line.priceMode, line.manualUnitPrice);
                return (
                  <Card key={line.id} variant="outlined">
                    <Box sx={{ p: 1 }}>
                      <Stack spacing={0.75}>
                        <Autocomplete
                          options={fgItems}
                          loading={fgLoading}
                          value={item}
                          disablePortal
                          isOptionEqualToValue={(opt, val) => String(opt?.id || opt?.documentId) === String(val?.id || val?.documentId)}
                          getOptionLabel={(opt) => `${opt?.sku || ""} - ${opt?.name || ""}`.trim() || String(opt?.id || "")}
                          onChange={(_, newVal) => {
                            const quick = newVal ? normalizeInventoryItemForProjection(newVal) : null;
                            setLine(line.id, { inventoryItem: quick });
                            if (quick?.documentId || quick?.id) {
                              (async () => {
                                try {
                                  const fresh = await fetchInventoryItemForProjection(quick.documentId || quick.id);
                                  if (fresh) setLine(line.id, { inventoryItem: fresh });
                                } catch (_) {}
                              })();
                            }
                          }}
                          renderInput={(params) => <TextField {...params} label="Finished good" />}
                        />

                        <Stack direction="row" spacing={0.75} sx={{ overflowX: "auto", pb: 0.25 }}>
                          <Chip size="small" variant="outlined" label={`Unit cost: ${formatMoney(item?.unitCost)}`} />
                          <Chip size="small" variant="outlined" label={`Core cost: ${formatMoney(item?.coreCost)}`} />
                          <Chip size="small" variant="outlined" label={`Core charge: ${formatMoney(item?.coreCharge)}`} />
                        </Stack>

                        <Stack direction="row" spacing={0.75}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Price mode</InputLabel>
                            <Select value={line.priceMode} label="Price mode" onChange={(e) => setLine(line.id, { priceMode: e.target.value })}>
                              <MenuItem value="unitPrice">Unit Price</MenuItem>
                              <MenuItem value="retail">Retail</MenuItem>
                              <MenuItem value="wholesale">Wholesale</MenuItem>
                              <MenuItem value="bulk">Bulk</MenuItem>
                              <MenuItem value="manual">Manual</MenuItem>
                            </Select>
                          </FormControl>
                          {line.priceMode === "manual" ? (
                            <TextField
                              label="Manual unit price"
                              type="number"
                              value={line.manualUnitPrice}
                              onChange={(e) => setLine(line.id, { manualUnitPrice: e.target.value })}
                              fullWidth
                            />
                          ) : (
                            <TextField label="Unit price" value={formatMoney(unitPrice)} fullWidth InputProps={{ readOnly: true }} />
                          )}
                        </Stack>

                        <Divider />

                        <Typography variant="caption" color="text.secondary">
                          Quantities by month
                        </Typography>
                        <Box sx={{ overflowX: "auto" }}>
                          <Stack direction="row" spacing={0.75} sx={{ pb: 0.5 }}>
                            {months.map((m) => (
                              <TextField
                                key={`${line.id}-${m}`}
                                label={monthLabel(m)}
                                type="number"
                                value={line.quantitiesByMonth?.[m] ?? ""}
                                onChange={(e) => setLineQty(line.id, m, e.target.value)}
                                inputProps={{ min: 0 }}
                                sx={{ minWidth: 130 }}
                              />
                            ))}
                          </Stack>
                        </Box>

                        <Button
                          variant="text"
                          color="error"
                          onClick={() => removeSalesLine(line.id)}
                          disabled={(scenario.salesLines || []).length <= 1}
                        >
                          Remove line
                        </Button>
                      </Stack>
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight={900}>
              Materials & cores (coverage)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.75}>
              {materials.missingBuildSheets?.length ? (
                <Alert severity="warning" variant="outlined">
                  Missing build sheets for: {materials.missingBuildSheets.slice(0, 8).join(", ")}
                  {materials.missingBuildSheets.length > 8 ? "…" : ""}
                </Alert>
              ) : null}
              {materials.missingCoreItems?.length ? (
                <Alert severity="warning" variant="outlined">
                  Missing core item links on FG items: {materials.missingCoreItems.slice(0, 8).join(", ")}
                  {materials.missingCoreItems.length > 8 ? "…" : ""}
                </Alert>
              ) : null}

              <Card variant="outlined">
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" fontWeight={900}>
                    Parts shortfall (cash)
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {formatMoney(materials.partsShortfallCashTotal)}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={0.75}>
                    {(materials.partsCoverageRows || []).slice(0, 12).map((r) => (
                      <Stack key={`p-${r.id}`} direction="row" justifyContent="space-between" spacing={0.75}>
                        <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                          {r.sku || r.name || r.id}
                        </Typography>
                        <Typography variant="caption" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
                          {formatMoney(r.shortfallCash)}
                        </Typography>
                      </Stack>
                    ))}
                    {(materials.partsCoverageRows || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No parts demand (or build sheets missing).
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Card>

              <Card variant="outlined">
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" fontWeight={900}>
                    Cores shortfall (cash)
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {formatMoney(materials.coreShortfallCashTotal)}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={0.75}>
                    {(materials.coreCoverageRows || []).slice(0, 12).map((r) => (
                      <Stack key={`c-${r.id}`} direction="row" justifyContent="space-between" spacing={0.75}>
                        <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                          {r.sku || r.name || r.id}
                        </Typography>
                        <Typography variant="caption" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
                          {formatMoney(r.shortfallCash)}
                        </Typography>
                      </Stack>
                    ))}
                    {(materials.coreCoverageRows || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No cores demand (or core links missing).
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Card>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight={900}>
              Month-by-month results
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Table size="small" sx={{ minWidth: 980 }}>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">COGS</TableCell>
                    <TableCell align="right">Core</TableCell>
                    <TableCell align="right">OpEx</TableCell>
                    <TableCell align="right">Cash-in</TableCell>
                    <TableCell align="right">Cash-out</TableCell>
                    <TableCell align="right">Net cash</TableCell>
                    <TableCell align="right">End cash</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectedByMonth.map((r) => (
                    <TableRow key={`m-${r.month}`} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{monthLabel(r.month)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatMoney(r.revenue)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatMoney(r.cogs)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatMoney(r.coreCost)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatMoney(r.opex)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatMoney(r.cashIn)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatMoney(r.cashOut)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 900 }}>
                        {formatMoney(r.netCash)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 900 }}>
                        {formatMoney(r.endingCash)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {projectedByMonth.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No months to display.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 1.25 }} />

            <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
              Inventory coverage (FG)
            </Typography>
            <Card variant="outlined">
              <Box sx={{ p: 1 }}>
                <Stack spacing={0.75}>
                  {(insights.inventoryCoverageRows || []).slice(0, 12).map((r) => (
                    <Stack key={`inv-${r.id}`} direction="row" justifyContent="space-between" spacing={0.75}>
                      <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                        {r.sku || r.name || r.id}
                      </Typography>
                      <Typography variant="caption" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
                        short {r.shortUnits} • {formatMoney(r.shortfallCash)}
                      </Typography>
                    </Stack>
                  ))}
                  {(insights.inventoryCoverageRows || []).length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No FG demand entered yet.
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            </Card>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Box>
  );
}

function MetricPill({ label, value }) {
  return (
    <Card variant="outlined" sx={{ flex: "0 0 auto" }}>
      <Box sx={{ px: 1.1, py: 0.85 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={900} sx={{ whiteSpace: "nowrap" }}>
          {value}
        </Typography>
      </Box>
    </Card>
  );
}

MetricPill.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};
